import appConfig from '../../config/appConfig';
import * as errorPageTypes from '../../consts/app/errorPageTypes';
import * as logMessageTypes from '../../consts/app/logMessageTypes';
import * as iccMethods from '../../consts/icc/iccMethods';
import {makeSelectHolderUamAuthToken, makeSelectUamAuthToken} from '../../state/selectors/auth';
import {makeSelectIotDeviceFullData} from '../../state/selectors/consumer';
import {getState} from '../../state/store';
import urlUtils from '../../utils/urlUtils';
import appInstanceService from '../app/appInstanceService';
import backendService from '../app/backendService';
import authDataService from '../auth/authDataService';
import authDataStoreService from '../auth/authDataStoreService';
import cmClientService from '../communicationLayer/cmClientService';
import HttpErrorHandlerService from '../httpErrorHandlerService';
import languageService from '../localization/languageService';
import log from '../logger/log';
import {authMapping, authUamMapping, loginTokenMapping} from '../mapping/iccMappings';
import appRouterService from '../route/appRouterService';
import server from '../server/server';
import yapWebSocketService from '../yap/yapWebSocketService';
import iccResponseErrorCheckService from './iccResponseErrorCheckService';

const getIccApiUrl = (methodName) => urlUtils.join(appConfig.getIccApiUrl(), methodName);

const getHeaders = (languageCode) => {
    const l = languageCode || languageService.getCurrentLanguageCode();

    return {
        a: appConfig.getIccApiHeader_a(),
        Accept: 'application/json',
        Authorization: appConfig.getIccApiHeaderAuthorization(),
        'Content-Type': 'application/x-www-form-urlencoded',
        l: l.toUpperCase(),
        m: appConfig.getIccApiHeader_m(),
        v: appConfig.getAppVersion(),
    };
};

const getBody = () => {
    return {
        grant_type: 'password',
        password: 'Any',
        scope: appConfig.getIccApiBodyScope(),
        username: appConfig.getIccApiHeaderUsername(),
    };
};

const onIccApiMethodResponse = (response) => {
    const responseData = authMapping(response.data);

    authDataStoreService.setIccTokenData(responseData);

    return responseData;
};

const fetchIccToken = async (languageCode) => {
    const data = urlUtils.stringify(getBody());

    try {
        const response = await getToken(data, getHeaders(languageCode));

        onIccApiMethodResponse(response);

        log.debug(
            `iccService: fetchIccToken, request: "${iccMethods.GET_TOKEN}" has successful response`,
            logMessageTypes.ICC
        );
    } catch (e) {
        log.error(`iccService: fetchIccToken, request: "${iccMethods.GET_TOKEN}", error: ${e}`, logMessageTypes.ICC);

        new HttpErrorHandlerService().handleError(e);
    }
};

const getDeviceData = (isHolder) => {
    const iotDevice = makeSelectIotDeviceFullData()(getState());
    const {deviceSerialNumber, codentifyOriginal} = (isHolder ? iotDevice.holder : iotDevice.device) || {};

    return {deviceSerialNumber, codentifyOriginal};
};

const isCodentifyNotValidForUam = (codentifyID) => {
    return !codentifyID || codentifyID.includes('*');
};

const getUamTokenData = (isHolder) => {
    const {codentifyOriginal: codentify, deviceSerialNumber: serial} = getDeviceData(isHolder);

    const sessionId = appInstanceService.getAppInstanceId();
    const data = {asset: {SessionID: sessionId}};
    const isCodentifyValidForUam = !isCodentifyNotValidForUam(codentify);

    if (isCodentifyValidForUam) {
        data.asset.IdType = 'TRACKING';
        data.asset.ID = codentify;
        return data;
    }

    if (serial) {
        data.asset.IdType = 'SERIAL';
        data.asset.ID = serial;
        return data;
    }

    const state = getState();
    const idToken = isHolder ? makeSelectHolderUamAuthToken()(state) : makeSelectUamAuthToken()(state);

    if (idToken) return;

    //holder was inserted after cloud pairing and codentify is not defined. Pairing must be started from the beginning
    // or codentify is encrypted with * by market
    log.info(`Bad Codentify: ${codentify} and token are not defined (isHolder: ${!!isHolder}).`, logMessageTypes.ICC);

    if (isHolder) {
        cmClientService.disconnectCmClient(true);
        yapWebSocketService.disconnectYapWebSocket(true);
    } else if (backendService.isUamBackend()) {
        appRouterService.forwardToErrorHandlingPage(errorPageTypes.ICC_TEMPORARY_ERROR);
    }
};

const fetchUamToken = async ({isRetry, isHolder} = {}) => {
    const uamTokenData = getUamTokenData(isHolder);

    if (!uamTokenData) return false;

    const appVersion = appConfig.getAppVersion();
    const iccMethod = iccMethods.GET_UAM_TOKEN_V3;
    const data = uamTokenData;
    const message = `iccService: fetchUamToken, request: "${iccMethod}" for ${JSON.stringify(uamTokenData)}`;

    try {
        const authToken = await authDataService.getAuthToken();
        const requestConfig = {
            headers: {Authorization: 'Bearer ' + authToken, v: appVersion},
            data,
        };
        const response = await server.post(getIccApiUrl(iccMethod), {...requestConfig});
        const responseData = authUamMapping(response.data);

        authDataStoreService.setUamApiData({...responseData, isHolder});

        log.debug(`${message} has successful response`, logMessageTypes.ICC);
        return response.data;
    } catch (e) {
        log.error(`${message}, error: ${e}`, logMessageTypes.ICC);
        if (isRetry) return false;

        await iccResponseErrorCheckService(e);
        return await fetchUamToken({isRetry: true, isHolder});
    }
};

const fetchSSOLink = async (deeplinkId) => {
    const appVersion = appConfig.getAppVersion();
    const authToken = await authDataService.getAuthToken();
    const headers = {Authorization: 'Bearer ' + authToken, v: appVersion};
    const login = authDataStoreService.getAuthUsername();
    const password = authDataStoreService.getSpiceToken();

    if (login) {
        const data = {
            Login: login,
            Password: password,
            AutoLoginLinkName: deeplinkId,
        };

        try {
            const response = await getLoginToken(data, headers);

            const {campaignUrlPersonal} = loginTokenMapping(response.data);

            log.debug(
                `iccService: fetchSSOLink, request: "${iccMethods.GET_CONSUMER_LOGIN_TOKEN}" has successful response`,
                logMessageTypes.ICC
            );

            return campaignUrlPersonal;
        } catch (e) {
            const errorStatus = e?.response?.status;

            log.error(
                `iccService: fetchSSOLink, request: "${iccMethods.GET_CONSUMER_LOGIN_TOKEN}", error: ${e}`,
                logMessageTypes.ICC
            );
            new HttpErrorHandlerService().handleError(e);

            return {errorStatus};
        }
    } else {
        log.debug(`iccService: fetchSSOLink is canceled, login is empty`, logMessageTypes.ICC);
    }
};

const getToken = (data, headers) => server.post(getIccApiUrl(iccMethods.GET_TOKEN), {data, headers});

const getLoginToken = (data, headers) =>
    server.post(getIccApiUrl(iccMethods.GET_CONSUMER_LOGIN_TOKEN), {data, headers});

export default {
    fetchIccToken,
    fetchSSOLink,
    fetchUamToken,
    isCodentifyNotValidForUam,
};
